import { useEffect, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import {
  Loader2,
  CalendarDays,
  Clock,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  LogOut,
  Inbox,
  Zap,
} from 'lucide-react';
import { Toaster } from './ui/sonner';
import appLogo from '@/assets/upstand_ai_logo.svg';

interface MeetingRef {
  id: string;
  starts_at: string;
  ends_at: string | null;
}

interface SeriesSummary {
  series_id: string;
  title: string;
  slug: string;
  is_organizer: boolean;
  next_meeting: MeetingRef | null;
  last_meeting: MeetingRef | null;
}

export function AttendeeAllMeetings() {
  const [phase, setPhase] = useState<'loading' | 'unauthenticated' | 'ready'>('loading');
  const [series, setSeries] = useState<SeriesSummary[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        setPhase('unauthenticated');
        return;
      }
      setUserEmail(session.user.email ?? null);

      const { data, error } = await supabase.rpc('attendee_meetings_for_self');
      if (error) {
        console.error('attendee_meetings_for_self failed', error);
        setSeries([]);
      } else {
        setSeries((data as SeriesSummary[]) ?? []);
      }
      setPhase('ready');
    })();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  if (phase === 'loading') {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-amber-700/70" />
          <p className="font-display text-sm text-stone-500">Loading your meetings…</p>
        </div>
      </Shell>
    );
  }

  if (phase === 'unauthenticated') {
    return (
      <Shell>
        <div className="w-full max-w-md rounded-xl border border-outline-variant/15 bg-white/60 p-8 shadow-sm backdrop-blur-xl">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50">
              <Inbox className="h-7 w-7 text-amber-700" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-light tracking-tight text-foreground">
                Open your meeting link
              </h2>
              <p className="mt-2 text-sm text-stone-500">
                To see all your meetings here, first sign in by opening the link from any of your
                calendar invites.
              </p>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  const hasNonOrganizerMeetings = series.some((s) => !s.is_organizer);

  return (
    <Shell signedIn signOut={signOut} email={userEmail}>
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-stone-400">
            Your meetings
          </p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-foreground">
            Welcome back
          </h1>
          {userEmail && (
            <p className="mt-2 text-sm text-stone-500">
              Showing meetings for{' '}
              <span className="font-medium text-foreground">{userEmail}</span>
            </p>
          )}
        </div>

        {series.length === 0 ? (
          <EmptyCard />
        ) : (
          <div className="space-y-3">
            {series.map((s) => (
              <SeriesCard key={s.series_id} series={s} />
            ))}
          </div>
        )}

        {/* Signup CTA - shown when attending someone else's meetings */}
        {hasNonOrganizerMeetings && <SignupCTA className="mt-6" />}
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Series card
// ---------------------------------------------------------------------------

function SeriesCard({ series }: { series: SeriesSummary }) {
  const focused = series.next_meeting ?? series.last_meeting;
  const isUpcoming = !!series.next_meeting;
  const href = `/meet/direct/${encodeURIComponent(series.slug)}`;

  return (
    <a
      href={href}
      className="group block rounded-xl border border-outline-variant/15 bg-white/60 p-5 shadow-sm backdrop-blur-xl transition-all hover:bg-white/80 hover:shadow-md"
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            isUpcoming ? 'bg-amber-50' : 'bg-stone-50'
          }`}
        >
          {isUpcoming ? (
            <Sparkles className="h-5 w-5 text-amber-700" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-stone-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                isUpcoming
                  ? 'border border-amber-100 bg-amber-50 text-amber-700'
                  : 'border border-stone-200 bg-stone-50 text-stone-500'
              }`}
            >
              {isUpcoming ? 'Upcoming' : 'Past'}
            </span>
            {series.is_organizer && (
              <span className="inline-block rounded-full border border-primary-container/40 bg-primary-container/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-primary">
                Organiser
              </span>
            )}
          </div>
          <h3 className="mt-1.5 font-display text-lg font-light tracking-tight text-foreground">
            {series.title}
          </h3>
          {focused?.starts_at && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-amber-700/60" />
                {new Date(focused.starts_at).toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-amber-700/60" />
                {new Date(focused.starts_at).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
                {focused.ends_at && (
                  <>
                    {' – '}
                    {new Date(focused.ends_at).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <ArrowRight className="mt-1.5 h-4 w-4 shrink-0 text-stone-300 transition-all group-hover:translate-x-1 group-hover:text-amber-700" />
      </div>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyCard() {
  return (
    <div className="rounded-xl border border-outline-variant/15 bg-white/60 p-10 shadow-sm backdrop-blur-xl">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-stone-50">
          <Inbox className="h-6 w-6 text-stone-400" />
        </div>
        <div>
          <h3 className="font-display text-xl font-light tracking-tight text-foreground">
            No meetings yet
          </h3>
          <p className="mt-1 max-w-sm text-sm text-stone-500">
            We didn't find any Upstand meetings for your email. Open a meeting link from your
            calendar invite to get started.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signup CTA
// ---------------------------------------------------------------------------

function SignupCTA({ className }: { className?: string }) {
  return (
    <div
      className={`rounded-xl border border-outline-variant/15 bg-white/60 p-6 shadow-sm backdrop-blur-xl ${className ?? ''}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50">
          <Zap className="h-5 w-5 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">
            Get started
          </p>
          <h3 className="mt-0.5 font-display text-lg font-light tracking-tight text-foreground">
            Run your own meetings with Upstand.ai
          </h3>
          <p className="mt-1.5 text-sm text-stone-500">
            AI-powered agendas, decisions, action items and check-ins for your team.
          </p>
          <a
            href="/"
            className="mt-4 inline-flex items-center gap-2 rounded-md px-5 py-2.5 font-display text-sm font-medium uppercase tracking-widest text-white onboarding-primary-gradient"
          >
            Sign up free
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

function Shell({
  children,
  signedIn,
  signOut,
  email,
}: {
  children: React.ReactNode;
  signedIn?: boolean;
  signOut?: () => void;
  email?: string | null;
}) {
  void email;
  return (
    <div className="flex min-h-screen flex-col bg-[#fbf9f5] font-sans text-foreground">
      <div className="pointer-events-none fixed inset-0 -z-10 onboarding-ethereal-glow" aria-hidden />
      <div
        className="pointer-events-none fixed top-20 right-10 -z-10 h-96 w-96 rounded-full bg-tertiary-container/10 blur-[100px]"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed bottom-20 left-10 -z-10 h-80 w-80 rounded-full bg-primary-container/10 blur-[80px]"
        aria-hidden
      />

      <header className="sticky top-0 z-50 flex w-full items-center justify-between border-b border-stone-100/60 bg-stone-50/80 px-6 py-4 backdrop-blur-xl sm:px-8 sm:py-5">
        <img
          src={appLogo}
          alt="Upstand.ai"
          className="h-10 w-auto max-w-[240px] object-contain object-left sm:h-12"
        />
        {signedIn && signOut && (
          <button
            type="button"
            onClick={signOut}
            className="flex items-center gap-1.5 text-xs font-medium text-stone-400 transition-colors hover:text-stone-700"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        )}
      </header>

      {signedIn ? (
        <main className="flex-1">{children}</main>
      ) : (
        <main className="flex flex-1 items-center justify-center p-6">{children}</main>
      )}

      <Toaster />
    </div>
  );
}
